import {GeospatialService} from '../../helper/geospatial-service';
import {AssetRendererService} from '../../renderers/asset-renderer.service';
import {AssetFieldsService} from '../../fields/asset-fields.service';

import {Injectable} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {SpatialEntityDataExtended} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {LayerSettings} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {VisualizationType} from '../../../../../../pages/geospatial-viewer/model/visualization';

@Injectable()
export class PointLayerService {
    constructor(
        private geospatialService: GeospatialService,
        private genericPopupService: GenericPopupService,
        private rendererService: AssetRendererService,
        private fieldsService: AssetFieldsService,
    ) {}

    /**
     * Create two point layers for asset population:
     * 1. Point "main" layer showing individual points
     * 2. Point "outline" layer to emphasize overlapping points
     * @return [FeatureLayer, FeatureLayer]
     */
    _preparePointSettings: LayerSettings = {
        showTooltip: true,
        suppressTooltipTitle: false,
        metrics: [],
        showStaticLabel: true,
        staticLabelProps: {
            expression: `if ($feature.count == 2)
                            {return Text('•')}`,
            labelPlacement: 'center-center',
            symbol: {
                type: 'text',
                color: 'black',
                font: {
                    weight: 'bold',
                    size: '12px',
                },
            },
        },
    };

    public preparePointLayers(
        pointData: SpatialEntityDataExtended[],
        renderedVisualizationType: VisualizationType,
        id: string,
        theme: string,
        settings?: LayerSettings,
    ): FeatureLayer[] {
        // Get settings: priority of provided settings, else default settings i.e. allows settings to be partial set
        const finalSettings: LayerSettings = {
            ...this._preparePointSettings,
            ...settings,
        };

        // Zoomed in: render point asset layer (+ outline as separate layer), and linear asset layer distinctly
        const pointSource = this.createPointGraphics(pointData, settings.metrics[0] || '');

        let pointPopupTemplate = null;
        if (finalSettings?.showTooltip) {
            pointPopupTemplate = {
                title: finalSettings?.suppressTooltipTitle ? '' : '{id}',
                outFields: ['*'],
                content: this.genericPopupService.getGenericMetricTooltipFunction.bind(
                    this.genericPopupService,
                    finalSettings?.metrics || [],
                    renderedVisualizationType,
                ),
            };
        }
        // ArcGIS does not support data-driven symbol / symbol.border, nor multi-variate rendering of symbols; use two layers.
        const pointAssetLayer = new FeatureLayer({
            title: 'asset_point_layer',
            id: id ? id : 'asset_point_layer',
            source: pointSource,
            renderer: this.rendererService.assetPointRenderer(theme, true),
            fields: this.fieldsService.getMapFields(),
            outFields: ['*'],
            popupTemplate: pointPopupTemplate,
            // @ts-ignore
            labelingInfo: finalSettings.staticLabelProps
                ? [
                      {
                          labelExpressionInfo: {
                              expression: finalSettings.staticLabelProps.expression,
                          },
                          symbol: finalSettings.staticLabelProps.symbol,
                          labelPlacement: finalSettings.staticLabelProps.labelPlacement,
                      },
                  ]
                : {},
        });

        const pointAssetOutlineLayer = new FeatureLayer({
            id: (id ? id : 'asset_point_layer') + 'outline',
            source: pointSource,
            renderer: this.rendererService.pointLayerOverlapRenderer(),
            fields: this.fieldsService.getMapFields(),
            popupTemplate: pointPopupTemplate,
        });

        return [pointAssetLayer, pointAssetOutlineLayer];
    }

    /**
     * Create single point layer with "bubble" style reduction
     * @return FeatureLayer
     */
    public preparePointBubbleLayer(data: SpatialEntityDataExtended[], theme: string): FeatureLayer {
        return new FeatureLayer({
            id: 'asset_bubble_layer',
            outFields: ['*'],
            objectIdField: 'ObjectID',
            source: this.createPointGraphics(data),
            renderer: this.rendererService.bubbleLayerRenderer(theme),
            fields: this.fieldsService.getMapFields(),
            labelingInfo: [
                {
                    labelExpressionInfo: {
                        expression: `Text($feature.count)`,
                    },
                    symbol: {
                        type: 'text',
                        color: '#444',
                        font: {
                            weight: 'normal',
                            size: '12px',
                        },
                    },
                    labelPlacement: 'center-center',
                },
            ],
        });
    }

    /*
     * Create array of Graphics, type = point. Return Graphic[]
     */
    private createPointGraphics(pointData: SpatialEntityDataExtended[], metric?: string): Graphic[] {
        return pointData.map((asset: SpatialEntityDataExtended) => {
            const point = new Point({
                latitude: asset.geoJsonGeometry.coordinates[1],
                longitude: asset.geoJsonGeometry.coordinates[0],
            });

            return new Graphic({
                geometry: point,
                attributes: {...asset, ...(!!metric ? {metric: metric} : {})},
            });
        });
    }
}
